import { Box, Divider, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router';
import { useAtom, useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { currentContextAtom } from '../state';
import { RoutePaths } from '../routes/AppRoutes';
import { ellipsisStyle, linkStyle } from '../common/utils/style.utils';
import { BuyingCompanyInfo, CompanyOfferInfo, currentChannelAtom, currentContractAtom } from './state';
import ChannelCompanyImage from './ChannelCompanyImage';
import ChannelActions from './ChannelActions';

export default function ChannelHeader(): React.JSX.Element {
  const { t } = useTranslation(['chat', 'common']);
  const { realm } = useAtomValue(currentContextAtom);
  const [currentChannel, setCurrentChannel] = useAtom(currentChannelAtom);
  const currentContract = useAtomValue(currentContractAtom);

  const companyInfo = useMemo((): CompanyOfferInfo | BuyingCompanyInfo | undefined => {
    if (!currentContract) {
      return;
    }

    return realm === Business_Profile_Enum.Buyer ? currentContract.supplying_company : currentContract.buying_company;
  }, [currentContract]);

  if (!currentChannel) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Box
      sx={{
        display: {
          xs: 'none',
          lg: 'flex',
        },
        flexDirection: 'column',
        px: { xs: 2, md: 3 },
        pt: { xs: 2, md: 3 },
        pb: 2,
        '& .MuiDivider-root:not(:last-child)': {
          mb: {
            xs: 2,
            sm: 3,
          },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {
          companyInfo && (
            <>
              <Button
                variant="text"
                onClick={() => setCurrentChannel(undefined)}
                sx={{
                  display: {
                    xs: 'inline-flex',
                    sm: 'none',
                  },
                }}
              >
                <ArrowBackIcon />
              </Button>
              <ChannelCompanyImage
                company={{
                  businessProfile: realm === Business_Profile_Enum.Buyer ? Business_Profile_Enum.Supplier : Business_Profile_Enum.Buyer,
                  photo: companyInfo.photos[0]?.photo,
                  brand: 'brand' in companyInfo ? companyInfo.brand : undefined,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  width: 'calc(100% - 56px)',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  ml: 1,
                }}
              >
                <Typography
                  variant="bodyLarge"
                  component={Link}
                  to={generatePath(RoutePaths.CONTRACT_COMPANY, { companyId: companyInfo.id })}
                  sx={{
                    ...linkStyle,
                  }}
                >
                  {companyInfo.tradeName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'space-between' }}>
                  <Typography
                    variant="bodyMedium"
                    color="secondary"
                    sx={{ flex: 1, ...ellipsisStyle }}
                  >
                    {t('chat:N_member', { count: currentChannel!.memberCount })}
                  </Typography>
                </Box>
              </Box>
              <ChannelActions companyName={companyInfo.tradeName} channel={currentChannel} />
            </>
          )
        }
      </Box>
      <Divider />
    </Box>
  );
}
